// 📄 pages/Auth/ActivarCuenta.tsx
// /activar?codigo=… (2026-10-26): quien fue invitado a un proyecto u
// organización crea aquí su contraseña. Pública (sin sesión): el código del
// correo es la prueba. Al terminar entra directo con su correo.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { KeyRound, RefreshCw, AlertTriangle } from 'lucide-react';
import { auth } from '../../firebase';
import { consultarActivacion, activarCuenta, DatosActivacion, PASSWORD_MIN } from '../../services/activacionService.ts';

export default function ActivarCuenta() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const codigo = params.get('codigo') || '';

  const [datos, setDatos] = useState<DatosActivacion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorEnlace, setErrorEnlace] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!codigo) {
      setErrorEnlace('Este enlace no es válido. Pide que te reenvíen la invitación.');
      setCargando(false);
      return;
    }
    consultarActivacion(codigo)
      .then(setDatos)
      .catch((err) => setErrorEnlace(err.message || 'No se pudo validar el enlace.'))
      .finally(() => setCargando(false));
  }, [codigo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < PASSWORD_MIN) { setError(`La contraseña debe tener al menos ${PASSWORD_MIN} caracteres.`); return; }
    if (password !== confirmar) { setError('Las contraseñas no coinciden.'); return; }

    setGuardando(true);
    try {
      const { email } = await activarCuenta(codigo, password);
      // Si había otra sesión abierta en este navegador, se cierra para entrar con la cuenta nueva.
      if (auth.currentUser && auth.currentUser.email?.toLowerCase() !== email) await signOut(auth);
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'No se pudo crear la contraseña.');
      setGuardando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="bg-gray-800 p-8 rounded-xl shadow-md w-full max-w-sm">
        {cargando ? (
          <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : errorEnlace ? (
          <div className="flex flex-col items-center text-center gap-3">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
            <h1 className="text-xl font-bold">No se pudo usar el enlace</h1>
            <p className="text-sm text-gray-400">{errorEnlace}</p>
            <button onClick={() => navigate('/', { replace: true })} className="mt-2 text-sm text-blue-400 hover:underline">
              Ir a OneHub
            </button>
          </div>
        ) : datos && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col items-center text-center gap-2 mb-2">
              <KeyRound className="w-8 h-8 text-blue-400" />
              <h1 className="text-xl font-bold">Hola {datos.nombre}, crea tu contraseña</h1>
              {datos.invitacion && (
                <p className="text-sm text-gray-400">Te invitaron a <span className="text-white font-medium">{datos.invitacion}</span> en OneHub.</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Correo</label>
              <input value={datos.email} disabled className="w-full p-2 rounded bg-gray-700 text-gray-300" />
            </div>
            <input
              type="password"
              placeholder={`Contraseña (mínimo ${PASSWORD_MIN} caracteres)`}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(null); }}
              className="w-full p-2 rounded bg-gray-700 text-white"
              autoComplete="new-password"
              autoFocus
              required
            />
            <input
              type="password"
              placeholder="Repite la contraseña"
              value={confirmar}
              onChange={(e) => { setConfirmar(e.target.value); setError(null); }}
              className="w-full p-2 rounded bg-gray-700 text-white"
              autoComplete="new-password"
              required
            />

            {error && <p className="text-sm text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={guardando}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 py-2 rounded font-semibold"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Crear contraseña y entrar'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
